// ============================================================================
// Amenidades — backend client. Talks to the company-scoped `amenities` module
// (apps/torre-de-control-api). `companyId` / auth headers are injected by the
// shared axios instances (utils/api). Photos are uploaded as multipart files
// (one indexed field each: photo0, photo1, …); kept URLs travel as
// `existingPhotos`. Maps the API document <-> the front-end `Amenity` shape.
// ============================================================================

import axios from "axios";
import api, { apiFormData } from "../../../utils/api";
import type { Amenity, Booking } from "../store";

const API_URL =
  import.meta.env.VITE_FIREBASE_API_URL ||
  "http://127.0.0.1:5001/cc-console-development/us-central1/wayakDevelopmentApi";

/** A bare axios client for the PUBLIC (no-auth) amenities endpoint. */
const publicApi = axios.create({ baseURL: API_URL, timeout: 30000 });

type ApiAmenity = Partial<Amenity> & { id: string };

/** Backend document → front-end Amenity (fills defaults for older docs). */
function fromApi(d: ApiAmenity): Amenity {
  return {
    id: d.id,
    name: d.name ?? "",
    type: d.type ?? "otro",
    description: d.description ?? "",
    location: d.location ?? "",
    price: Number(d.price ?? 0),
    priceUnit: d.priceUnit ?? "por_reserva",
    currency: d.currency ?? "MXN",
    capacity: Number(d.capacity ?? 1),
    minMinutes: Number(d.minMinutes ?? 60),
    maxMinutes: Number(d.maxMinutes ?? 120),
    openTime: d.openTime ?? "08:00",
    closeTime: d.closeTime ?? "20:00",
    days: Array.isArray(d.days) ? d.days : [],
    maxAdvanceDays: Number(d.maxAdvanceDays ?? 30),
    bufferMinutes: Number(d.bufferMinutes ?? 0),
    rules: d.rules ?? "",
    cancellationPolicy: d.cancellationPolicy ?? "",
    deposit: Number(d.deposit ?? 0),
    requirements: d.requirements ?? "",
    photos: Array.isArray(d.photos) ? d.photos : [],
    status: (d.status as Amenity["status"]) ?? "Disponible",
    visiblePublic: d.visiblePublic !== false,
    base_id: d.base_id ?? null,
  };
}

/** Serialize a draft (minus photos) into a FormData for multipart write. */
function toFormData(
  draft: Omit<Amenity, "id">,
  newFiles: File[],
): FormData {
  const form = new FormData();
  form.append("name", draft.name);
  form.append("type", draft.type);
  form.append("description", draft.description ?? "");
  form.append("location", draft.location ?? "");
  form.append("capacity", String(draft.capacity));
  form.append("price", String(draft.price));
  form.append("priceUnit", draft.priceUnit ?? "por_reserva");
  form.append("currency", draft.currency);
  form.append("minMinutes", String(draft.minMinutes));
  form.append("maxMinutes", String(draft.maxMinutes));
  form.append("openTime", draft.openTime);
  form.append("closeTime", draft.closeTime);
  form.append("days", JSON.stringify(draft.days ?? []));
  form.append("maxAdvanceDays", String(draft.maxAdvanceDays ?? 30));
  form.append("bufferMinutes", String(draft.bufferMinutes ?? 0));
  form.append("rules", draft.rules ?? "");
  form.append("cancellationPolicy", draft.cancellationPolicy ?? "");
  form.append("deposit", String(draft.deposit ?? 0));
  form.append("requirements", draft.requirements ?? "");
  form.append("status", draft.status);
  form.append("visiblePublic", String(draft.visiblePublic !== false));
  if (draft.base_id) form.append("base_id", draft.base_id);
  // URLs the user kept (already-uploaded photos), as JSON.
  form.append("existingPhotos", JSON.stringify(draft.photos ?? []));
  // New uploads — one indexed field each so they aren't overwritten server-side.
  newFiles.forEach((file, i) => form.append(`photo${i}`, file));
  return form;
}

/** List the company's amenities (auth + companyId injected by the interceptor). */
export async function listAmenities(): Promise<Amenity[]> {
  const res = await api.get("/amenitiesList");
  const body = res.data ?? {};
  const arr = Array.isArray(body.data) ? body.data : [];
  return arr.map(fromApi);
}

export async function createAmenity(
  draft: Omit<Amenity, "id">,
  newFiles: File[] = [],
): Promise<Amenity> {
  const res = await apiFormData.post("/amenity", toFormData(draft, newFiles));
  return fromApi(res.data?.data);
}

export async function updateAmenity(
  id: string,
  draft: Omit<Amenity, "id">,
  newFiles: File[] = [],
): Promise<Amenity> {
  const res = await apiFormData.put("/amenity", toFormData(draft, newFiles), {
    params: { id },
  });
  return fromApi(res.data?.data);
}

export async function deleteAmenity(id: string): Promise<void> {
  await api.delete("/amenity", { params: { id } });
}

/** Horario ocupado de una amenidad (página pública: sin datos de quien reservó). */
export interface BusySlot {
  amenityId: string;
  date: string;
  startTime: string;
  endTime: string;
}

/** PUBLIC (no auth) — visible amenities for a tenant + busy slots, for the public page. */
export async function fetchPublicSpaces(companyId: string): Promise<{ amenities: Amenity[]; busy: BusySlot[] }> {
  const res = await publicApi.get(`/public/amenities/${encodeURIComponent(companyId)}`);
  const body = res.data ?? {};
  const arr = Array.isArray(body.data) ? body.data : [];
  return { amenities: arr.map(fromApi), busy: Array.isArray(body.busy) ? body.busy : [] };
}

/** PUBLIC (no auth) — visible amenities for a tenant, for the public page. */
export async function fetchPublicAmenities(companyId: string): Promise<Amenity[]> {
  return (await fetchPublicSpaces(companyId)).amenities;
}

/** PUBLIC (no auth) — booking request; the server sets channel, status and price. */
export async function requestPublicBooking(
  companyId: string,
  draft: Pick<Booking, "amenityId" | "customerName" | "customerEmail" | "date" | "startTime" | "endTime" | "notes"> & { website?: string },
): Promise<Booking> {
  const { amenityId, customerName, customerEmail, date, startTime, endTime, notes, website } = draft;
  const res = await publicApi.post(`/public/amenities/${encodeURIComponent(companyId)}/booking`, { amenityId, customerName, customerEmail, date, startTime, endTime, notes, website });
  return res.data?.data as Booking;
}

// ---------------------------------------------------------------------------
// Bookings (amenity_bookings) — company-scoped, server allocates invoice folios.
// ---------------------------------------------------------------------------

/**
 * Reservas de la compañía. Sin `from`/`to` el servidor toma de −90 a +180 días
 * (ordenadas por fecha, hasta 1000) y avisa con `hasMore` si quedaron más.
 */
export async function listBookings(params: { from?: string; to?: string; amenityId?: string } = {}): Promise<{ items: Booking[]; hasMore: boolean; from?: string; to?: string }> {
  const res = await api.get("/amenityBookingsList", { params });
  const body = res.data ?? {};
  return {
    items: Array.isArray(body.data) ? (body.data as Booking[]) : [],
    hasMore: body.meta?.hasMore === true,
    from: body.meta?.from,
    to: body.meta?.to,
  };
}

/** Ajustes de la compañía: página pública de espacios (apagada por defecto). */
export async function getSpacesSettings(): Promise<{ publicPage: boolean }> {
  const res = await api.get("/bases/settings");
  return { publicPage: res.data?.data?.publicPage === true };
}

export async function setPublicPage(publicPage: boolean): Promise<{ publicPage: boolean }> {
  const res = await api.put("/bases/settings", { publicPage });
  return { publicPage: res.data?.data?.publicPage === true };
}

export async function createBooking(draft: Omit<Booking, "id">, issueInvoice = false): Promise<Booking> {
  const res = await api.post("/amenityBooking", { ...draft, issueInvoice });
  return res.data?.data as Booking;
}

export async function updateBooking(id: string, patch: Partial<Booking>, issueInvoice = false): Promise<Booking> {
  const res = await api.put("/amenityBooking", { ...patch, issueInvoice }, { params: { id } });
  return res.data?.data as Booking;
}

export async function deleteBooking(id: string): Promise<void> {
  await api.delete("/amenityBooking", { params: { id } });
}
